// crewbit-admin/src/hooks/useUpdateUserMoneyPreferences.ts
//
// Session 137 §Settings hub substrate — Money Module RPC-routed user
// preference mutations (admin / operator).
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §Settings.5.
// Mirrors crewbit-mobile/hooks/useUpdateUserMoneyPreferences.ts.
//
// Inline mutation hook per §3f LOCKED. Web-runtime divergence:
// window.dispatchEvent instead of DeviceEventEmitter.

import { useCallback } from 'react';
import { callMoneyRpc, type MoneyRpcResult } from '../lib/moneyRpc';
import { MONEY_MUTATION_EVENT } from './useFinancialSummary';

export type MoneyHeroDefault = 'revenue_month' | 'outstanding' | 'available_balance' | 'net_30d';
export type MoneyPeriodDefault = '7d' | '30d' | 'mtd' | 'qtd' | 'ytd';

export interface UpdateUserMoneyPreferencesArgs {
  moneyHeroDefault?: MoneyHeroDefault | null;
  moneyPeriodDefault?: MoneyPeriodDefault | null;
  notificationPaymentsReceived?: boolean | null;
  notificationDisputesOpened?: boolean | null;
  notificationTaxDue?: boolean | null;
  notificationPayoutsArrived?: boolean | null;
  // Tier 3 Phase B (Migration 278): accountant invite-flow notification flags.
  notificationAccountantInvited?: boolean | null;
  notificationAccountantAvailable?: boolean | null;
  // §Settings Phase 2B (Migration 20260519021118): general-category
  // notifications absorbed from the retired notification_preferences JSONB.
  pushEnabled?: boolean | null;
  notificationScheduleChanges?: boolean | null;
  notificationNewJobAssignments?: boolean | null;
  notificationBookingConfirmations?: boolean | null;
  notificationShiftReminders?: boolean | null;
  notificationLeaveRequestUpdates?: boolean | null;
  notificationTeamAnnouncements?: boolean | null;
  notificationDispatchAlerts?: boolean | null;
}

export type UpdateUserMoneyPreferencesResult = MoneyRpcResult<{
  user_id: string;
  updated_fields: string[];
}>;

export function useUpdateUserMoneyPreferences() {
  return useCallback(
    async (args: UpdateUserMoneyPreferencesArgs): Promise<UpdateUserMoneyPreferencesResult> => {
      const result = await callMoneyRpc<{
        user_id: string;
        updated_fields: string[];
      }>('update_user_money_preferences', {
        p_money_hero_default: args.moneyHeroDefault,
        p_money_period_default: args.moneyPeriodDefault,
        p_notification_payments_received: args.notificationPaymentsReceived,
        p_notification_disputes_opened: args.notificationDisputesOpened,
        p_notification_tax_due: args.notificationTaxDue,
        p_notification_payouts_arrived: args.notificationPayoutsArrived,
        p_notification_accountant_invited: args.notificationAccountantInvited,
        p_notification_accountant_available: args.notificationAccountantAvailable,
        // §Settings Phase 2B additions:
        p_push_enabled: args.pushEnabled,
        p_notification_schedule_changes: args.notificationScheduleChanges,
        p_notification_new_job_assignments: args.notificationNewJobAssignments,
        p_notification_booking_confirmations: args.notificationBookingConfirmations,
        p_notification_shift_reminders: args.notificationShiftReminders,
        p_notification_leave_request_updates: args.notificationLeaveRequestUpdates,
        p_notification_team_announcements: args.notificationTeamAnnouncements,
        p_notification_dispatch_alerts: args.notificationDispatchAlerts,
      });
      if (result.success) {
        window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
      }
      return result;
    },
    [],
  );
}
